import { HabitType, SprintTargets, HabitEntry } from '../types/centurion'

// --- Timezone & Date Utilities ---

/**
 * Returns the current date string in YYYY-MM-DD format for a specific timezone.
 * Defaults to Asia/Kolkata (IST).
 */
export function getLocalDateString(
  date: Date = new Date(),
  timeZone: string = 'Asia/Kolkata',
): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date)
}

/**
 * Calculates the number of days between two YYYY-MM-DD date strings.
 */
export function getDaysBetween(
  startDateStr: string,
  endDateStr: string,
): number {
  const start = new Date(`${startDateStr}T00:00:00Z`)
  const end = new Date(`${endDateStr}T00:00:00Z`)
  const diffTime = end.getTime() - start.getTime()
  return Math.floor(diffTime / (1000 * 60 * 60 * 24))
}

// --- Game Mechanics ---

/**
 * Determines the current sprint (1-4) based on the day number (1-100).
 */
export function getSprint(dayNumber: number): number {
  if (dayNumber <= 25) return 1
  if (dayNumber <= 50) return 2
  if (dayNumber <= 75) return 3
  if (dayNumber <= 100) return 4
  return 4 // Cap at 4 if they somehow go over 100
}

/**
 * Returns the target values for a given sprint.
 */
export function getSprintTargets(sprint: number): SprintTargets {
  switch (sprint) {
    case 1:
      return {
        protein: 100,
        water: 3.0,
        workout1: 45,
        workout2: 20,
        reading: 5,
        skillWork: 15,
      }
    case 2:
      return {
        protein: 125,
        water: 3.5,
        workout1: 55,
        workout2: 25,
        reading: 10,
        skillWork: 20,
      }
    case 3:
      return {
        protein: 150,
        water: 4.0,
        workout1: 65,
        workout2: 30,
        reading: 15,
        skillWork: 25,
      }
    case 4:
      return {
        protein: 175,
        water: 4.5,
        workout1: 75,
        workout2: 35,
        reading: 20,
        skillWork: 30,
      }
    default:
      return {
        protein: 100,
        water: 3.0,
        workout1: 45,
        workout2: 20,
        reading: 5,
        skillWork: 15,
      }
  }
}

/**
 * Generates a fresh set of habit entries for a new day based on the current sprint.
 */
export function generateDailyHabits(
  dayNumber: number,
): Record<HabitType, HabitEntry> {
  const sprint = getSprint(dayNumber)
  const targets = getSprintTargets(sprint)

  return {
    noSugar: { type: 'noSugar', value: false, target: true, completed: false },
    noAlcohol: {
      type: 'noAlcohol',
      value: false,
      target: true,
      completed: false,
    },
    protein: {
      type: 'protein',
      value: 0,
      target: targets.protein,
      completed: false,
    },
    water: { type: 'water', value: 0, target: targets.water, completed: false },
    workout1: {
      type: 'workout1',
      value: 0,
      target: targets.workout1,
      completed: false,
    },
    workout2: {
      type: 'workout2',
      value: 0,
      target: targets.workout2,
      completed: false,
    },
    reading: {
      type: 'reading',
      value: 0,
      target: targets.reading,
      completed: false,
    },
    skillWork: {
      type: 'skillWork',
      value: 0,
      target: targets.skillWork,
      completed: false,
    },
  }
}

/**
 * Checks if a daily log is fully complete (all habits met).
 */
export function isLogComplete(habits: Record<HabitType, HabitEntry>): boolean {
  return Object.values(habits).every((h) => h.completed)
}
